import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import {
    LayoutDashboard,
    Car,
    Map,
    Calendar,
    Mail,
    DollarSign,
    Settings,
    LogOut,
    ArrowLeft,
    Menu,
    X,
    ShieldCheck,
    PlaneTakeoff,
    MapPin,
    Star,
    Sparkles,
    BarChart3,
    Image as ImageIcon,
} from "lucide-react";
import { useAdminAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";

const NAV = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/cabs", label: "Manage Cabs", icon: Car },
    { to: "/admin/transfers", label: "Manage Transfers", icon: PlaneTakeoff },
    { to: "/admin/packages", label: "Manage Packages", icon: Map },
    { to: "/admin/destinations", label: "Destinations", icon: MapPin },
    { to: "/admin/popular-destinations", label: "Popular (Home)", icon: Sparkles },
    { to: "/admin/reviews", label: "Reviews", icon: Star },
    { to: "/admin/bookings", label: "Bookings", icon: Calendar },
    { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { to: "/admin/hero-images", label: "Hero images", icon: ImageIcon },
    { to: "/admin/inquiries", label: "Inquiries", icon: Mail },
    { to: "/admin/pricing", label: "Pricing", icon: DollarSign },
    { to: "/admin/settings", label: "Site Settings", icon: Settings },
];

const AdminLayout = () => {
    const { admin, adminLogout } = useAdminAuth();
    const { refreshAdminAll } = useData();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (admin) refreshAdminAll();
    }, [admin, refreshAdminAll]);

    if (!admin) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-slate-50 font-body" data-testid="admin-layout">
            {/* Mobile header */}
            <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 py-3 sticky top-0 z-30">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-saffron-500 text-white flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4" />
                    </div>
                    <span className="font-display font-semibold text-ink">SouthTrip Admin</span>
                </div>
                <button onClick={() => setOpen(!open)} className="p-2 rounded-md hover:bg-slate-100">
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </header>

            <div className="flex">
                {/* Sidebar */}
                <aside className={`${open ? "block" : "hidden"} lg:block fixed inset-0 z-20 lg:relative lg:inset-auto bg-white border-r border-slate-200 w-64 lg:min-h-screen lg:sticky lg:top-0`}>
                    <div className="hidden lg:flex items-center gap-2 px-5 py-5 border-b border-slate-200">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-saffron-500 to-saffron-600 text-white flex items-center justify-center shadow-sm">
                            <ShieldCheck className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="font-display text-lg font-semibold text-ink">SouthTrip</div>
                            <div className="text-[10px] uppercase tracking-widest text-ink-light">Admin Panel</div>
                        </div>
                    </div>
                    <nav className="p-3 space-y-0.5 mt-2 lg:mt-0">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.end}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                                        isActive
                                            ? "bg-saffron-50 text-saffron-700 border border-saffron-200"
                                            : "text-ink-light hover:bg-slate-100 hover:text-ink"
                                    }`
                                }
                                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <n.icon className="w-4 h-4" />
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="absolute lg:relative bottom-0 left-0 right-0 p-3 border-t border-slate-200 bg-white space-y-1">
                        <button
                            onClick={() => navigate("/")}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-ink-light hover:bg-slate-100"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back to website
                        </button>
                        <button
                            onClick={() => {
                                adminLogout();
                                navigate("/admin/login");
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-red-600 hover:bg-red-50"
                            data-testid="admin-logout"
                        >
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                </aside>

                {/* Content */}
                <main className="flex-1 min-w-0">
                    <div className="px-4 sm:px-6 lg:px-10 py-8">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
